// Civil Communicator PDF → canonical corpus.csv.
//
// Direct port of evals/adapters/civil_communicator.py — keep the parsing
// logic in lockstep so a PDF run through the browser yields a byte-
// identical canonical CSV to the desktop / Python adapter.
//
// Notable shape differences from the OFW adapter:
// - CC CSV has 5 columns (timestamp, sender, recipient, body, attachments);
//   no "subject" column.
// - CC declares parties by FREQUENCY of the "From" field across stanzas
//   (top two senders), rather than reading them off a cover page.
// - CC has a rejection sentinel — the Forwarded body literal "Rejected - The
//   communication was not forwarded." → row kept with empty body and
//   is_rejected=true in metadata.
// - CC has "Monitoring Override" as a synthetic Review Action prefix.
import { DateTime, IANAZone } from "luxon";

import { sanitizeActor, sanitizeBody } from "./_normalize";
import { extractPdfTextEol, type PdfExtractProgress } from "./_pdf";

export const PARSER_VERSION = "0.1.0";
export const ADAPTER_NAME = "civil-communicator";

const HEADER_LINE = "Civil Communicator";
const FOOTER_RE = /^https:\/\/communicator\.knack\.com\/.*$/;
const COMM_ID_LINE_RE_GLOBAL = /^Comm ID \d+$/gm;

const REQUIRED_LABELS = [
  "Comm ID",
  "Date/Time",
  "From",
  "Category",
  "Sub-Category",
  "Forwarded",
] as const;
const TERMINAL_LABELS = ["Review Action", "Monitoring Override"] as const;
const OPTIONAL_LABELS = ["File", "Library ID"] as const;
const ALL_LABELS: readonly string[] = [
  ...REQUIRED_LABELS,
  ...TERMINAL_LABELS,
  ...OPTIONAL_LABELS,
];

const REJECTED_SENTINEL = "Rejected - The communication was not forwarded.";

// Map North American timezone abbreviations Civil Communicator emits to
// IANA names luxon can resolve. Mirrors evals.adapters.civil_communicator
// _TZINFOS.
const TZ_ABBREV_TO_IANA: Record<string, string> = {
  EDT: "America/New_York",
  EST: "America/New_York",
  CDT: "America/Chicago",
  CST: "America/Chicago",
  MDT: "America/Denver",
  MST: "America/Denver",
  PDT: "America/Los_Angeles",
  PST: "America/Los_Angeles",
};

// "MM/DD/YYYY, HH:MM AM/PM TZ" — the format Civil Communicator emits.
const DATETIME_RE =
  /^(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2}):(\d{2})\s*(AM|PM)\s+([A-Z]{3,4})$/i;

export class MalformedCivilCommunicatorError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = "MalformedCivilCommunicatorError";
  }
}

export class MalformedStanzaError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = "MalformedStanzaError";
  }
}

// --- Canonical row + metadata shapes ---

export interface CanonicalRow {
  timestamp: string;
  sender: string;
  recipient: string;
  body: string;
  attachments: string;
}

export interface AttachmentStructured {
  filename: string;
  library_id: string | null;
  content: null;
}

export interface SourceMetadata {
  source: string;
  comm_id: string;
  category: string;
  sub_category: string;
  review_action: string;
  is_rejected: boolean;
  sender_role: "party" | "other";
  attachments_structured: AttachmentStructured[];
}

export interface ParsedStanza {
  comm_id: string;
  date_time_raw: string;
  from_name: string;
  category: string;
  sub_category: string;
  forwarded: string;
  review_action: string;
  file: string | null;
  library_id: string | null;
}

export interface IngestStats {
  party_a: string;
  party_b: string;
  out_of_set_senders: string[];
  message_count: number;
  accepted_count: number;
  rejected_count: number;
  non_printables_stripped: number;
  rejection_rate_by_party: Record<string, number>;
  date_range: { start: string | null; end: string | null };
}

export interface CaseMeta {
  id: string;
  privacy_class: string;
  prompt_vars: {
    party_a: string;
    party_b: string;
    format: string;
    time_period: string;
    context: string;
  };
  known_patterns: string[];
  notes: string;
}

export interface IngestResult {
  rows: CanonicalRow[];
  source_metas: SourceMetadata[];
  stats: IngestStats;
  csv: string;
  case_meta: CaseMeta;
  /** Parse-time warnings (dup comm_ids, malformed stanzas) for surfacing in UI. */
  warnings: string[];
}

// --- Stage 1: extract + clean pages ---

function cleanPages(pages: string[]): string[] {
  return pages.map((raw) =>
    raw
      .split("\n")
      .filter((line) => {
        const t = line.trim();
        return t !== HEADER_LINE && !FOOTER_RE.test(t);
      })
      .join("\n"),
  );
}

// --- Stage 2: concatenate ---

function concatenatePages(pages: string[]): string {
  return pages.join("\n");
}

// --- Stage 3: split stanzas ---

function splitStanzas(combined: string): string[] {
  const matches = Array.from(combined.matchAll(COMM_ID_LINE_RE_GLOBAL));
  if (matches.length === 0) return [];
  const out: string[] = [];
  for (let i = 0; i < matches.length; i++) {
    const m = matches[i];
    const start = m.index ?? 0;
    const end =
      i + 1 < matches.length ? matches[i + 1].index ?? combined.length : combined.length;
    out.push(combined.slice(start, end).trim());
  }
  return out;
}

// --- Stage 4: parse stanza fields ---

function parseStanzaFields(raw: string): ParsedStanza {
  const lines = raw.split("\n");
  const fields: Record<string, string> = {};

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    let matchedLabel: string | null = null;
    for (const label of ALL_LABELS) {
      if (line.startsWith(label + " ") || line === label) {
        matchedLabel = label;
        break;
      }
    }
    if (matchedLabel === null) {
      i += 1;
      continue;
    }

    // Value starts on same line (after the label).
    const valueFirst = line.slice(matchedLabel.length).replace(/^\s+/, "");
    if (matchedLabel === "Forwarded") {
      // Forwarded consumes lines until a terminal label.
      const valueLines: string[] = valueFirst ? [valueFirst] : [];
      let j = i + 1;
      while (
        j < lines.length &&
        !TERMINAL_LABELS.some((t) => lines[j].startsWith(t))
      ) {
        valueLines.push(lines[j]);
        j += 1;
      }
      fields.Forwarded = valueLines.join("\n").trim();
      i = j;
      continue;
    }
    fields[matchedLabel] = valueFirst;
    i += 1;
  }

  const missing = REQUIRED_LABELS.filter((lbl) => !(lbl in fields));
  if (missing.length > 0) {
    throw new MalformedStanzaError(
      `Missing required label(s): ${missing.join(", ")}. Stanza start: ${JSON.stringify(lines.slice(0, 2))}`,
    );
  }
  const terminalsPresent = TERMINAL_LABELS.filter((t) => t in fields);
  if (terminalsPresent.length === 0) {
    throw new MalformedStanzaError(
      `Missing terminal label (one of ${TERMINAL_LABELS.join(", ")}). Stanza start: ${JSON.stringify(lines.slice(0, 2))}`,
    );
  }

  const reviewAction =
    "Review Action" in fields
      ? fields["Review Action"]
      : `Override-${fields["Monitoring Override"]}`;

  return {
    comm_id: fields["Comm ID"],
    date_time_raw: fields["Date/Time"],
    from_name: fields.From,
    category: fields.Category,
    sub_category: fields["Sub-Category"],
    forwarded: fields.Forwarded,
    review_action: reviewAction,
    file: fields.File ?? null,
    library_id: fields["Library ID"] ?? null,
  };
}

// --- Timestamp ---

function parseTimestamp(raw: string): string {
  const m = raw.match(DATETIME_RE);
  if (!m) {
    throw new MalformedCivilCommunicatorError(
      `unparseable datetime: ${JSON.stringify(raw)}`,
    );
  }
  const [, monthS, dayS, yearS, hourS, minuteS, meridian, tzAbbrev] = m;
  let hour = parseInt(hourS, 10);
  if (meridian.toUpperCase() === "PM" && hour !== 12) hour += 12;
  else if (meridian.toUpperCase() === "AM" && hour === 12) hour = 0;

  const iana = TZ_ABBREV_TO_IANA[tzAbbrev.toUpperCase()];
  if (!iana || !IANAZone.isValidZone(iana)) {
    throw new MalformedCivilCommunicatorError(
      `unknown timezone abbreviation: ${tzAbbrev}`,
    );
  }
  const local = DateTime.fromObject(
    {
      year: parseInt(yearS, 10),
      month: parseInt(monthS, 10),
      day: parseInt(dayS, 10),
      hour,
      minute: parseInt(minuteS, 10),
      second: 0,
    },
    { zone: iana },
  );
  if (!local.isValid) {
    throw new MalformedCivilCommunicatorError(
      `bad datetime ${raw}: ${local.invalidExplanation ?? local.invalidReason}`,
    );
  }
  return local.toUTC().toFormat("yyyy-LL-dd'T'HH:mm:ss'Z'");
}

// --- Party detection (frequency-based, ties broken by first-seen index) ---

function determineDeclaredParties(
  stanzas: ParsedStanza[],
): { party_a: string; party_b: string; out_of_set: string[] } {
  if (stanzas.length === 0) {
    return { party_a: "", party_b: "", out_of_set: [] };
  }
  const counts = new Map<string, number>();
  const firstSeen = new Map<string, number>();
  stanzas.forEach((s, idx) => {
    counts.set(s.from_name, (counts.get(s.from_name) ?? 0) + 1);
    if (!firstSeen.has(s.from_name)) firstSeen.set(s.from_name, idx);
  });
  const ordered = [...counts.entries()].sort((a, b) => {
    if (b[1] !== a[1]) return b[1] - a[1];
    return (firstSeen.get(a[0]) ?? 0) - (firstSeen.get(b[0]) ?? 0);
  });
  const party_a = ordered[0]?.[0] ?? "";
  const party_b = ordered[1]?.[0] ?? "";
  const declared = new Set([party_a, party_b]);
  const out_of_set = [...counts.keys()].filter((n) => !declared.has(n)).sort();
  return { party_a, party_b, out_of_set };
}

// --- Body normalization ---

function bodyFromForwarded(forwarded: string): [string, boolean] {
  const stripped = forwarded.trim();
  if (stripped === REJECTED_SENTINEL) return ["", true];
  // Collapse all whitespace runs to a single space.
  const joined = stripped.replace(/\s+/g, " ");
  return [joined, false];
}

// --- Attachments ---

function attachmentsSummaryAndStructured(
  file: string | null,
  library_id: string | null,
): [string, AttachmentStructured[]] {
  if (file === null) return ["", []];
  const summary = library_id ? `${file} (Library ID ${library_id})` : file;
  const structured: AttachmentStructured[] = [
    { filename: file, library_id, content: null },
  ];
  return [summary, structured];
}

// --- Stage 5: build rows + metadata + stats ---

function buildRowsAndMetadata(
  stanzas: ParsedStanza[],
): { rows: CanonicalRow[]; metas: SourceMetadata[]; stats: IngestStats } {
  const { party_a, party_b, out_of_set } = determineDeclaredParties(stanzas);
  const declaredSet = new Set([party_a, party_b]);
  let nonPrintablesStripped = 0;

  type Provisional = { timestamp: string; row: CanonicalRow; meta: SourceMetadata };
  const provisional: Provisional[] = [];
  const seenCommIds = new Set<string>();

  for (const s of stanzas) {
    if (seenCommIds.has(s.comm_id)) continue;
    seenCommIds.add(s.comm_id);

    const timestamp = parseTimestamp(s.date_time_raw);
    const [rawBody, isRejected] = bodyFromForwarded(s.forwarded);
    const [body, nBody] = sanitizeBody(rawBody);
    nonPrintablesStripped += nBody;
    const [attsSummary, attsStructured] = attachmentsSummaryAndStructured(
      s.file,
      s.library_id,
    );

    const [sender, nSender] = sanitizeActor(s.from_name);
    nonPrintablesStripped += nSender;

    let senderRole: "party" | "other";
    let recipient = "";
    if (declaredSet.has(sender) && sender !== "") {
      senderRole = "party";
      recipient = sender === party_a ? party_b : party_a;
    } else {
      senderRole = "other";
      recipient = "";
    }

    provisional.push({
      timestamp,
      row: {
        timestamp,
        sender,
        recipient,
        body,
        attachments: attsSummary,
      },
      meta: {
        source: ADAPTER_NAME,
        comm_id: s.comm_id,
        category: s.category,
        sub_category: s.sub_category,
        review_action: s.review_action,
        is_rejected: isRejected,
        sender_role: senderRole,
        attachments_structured: attsStructured,
      },
    });
  }

  provisional.sort((a, b) =>
    a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0,
  );
  const rows = provisional.map((p) => p.row);
  const metas = provisional.map((p) => p.meta);

  const accepted = metas.filter((m) => !m.is_rejected).length;
  const rejected = metas.length - accepted;

  const perPartyTotals = new Map<string, number>();
  const perPartyRejected = new Map<string, number>();
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const m = metas[i];
    if (m.sender_role === "party") {
      perPartyTotals.set(r.sender, (perPartyTotals.get(r.sender) ?? 0) + 1);
      if (m.is_rejected) {
        perPartyRejected.set(
          r.sender,
          (perPartyRejected.get(r.sender) ?? 0) + 1,
        );
      }
    }
  }
  const rejectionRateByParty: Record<string, number> = {};
  for (const [name, total] of perPartyTotals) {
    rejectionRateByParty[name] = (perPartyRejected.get(name) ?? 0) / total;
  }

  const date_range = {
    start: rows.length > 0 ? rows[0].timestamp : null,
    end: rows.length > 0 ? rows[rows.length - 1].timestamp : null,
  };

  return {
    rows,
    metas,
    stats: {
      party_a,
      party_b,
      out_of_set_senders: out_of_set,
      message_count: rows.length,
      accepted_count: accepted,
      rejected_count: rejected,
      non_printables_stripped: nonPrintablesStripped,
      rejection_rate_by_party: rejectionRateByParty,
      date_range,
    },
  };
}

// --- CSV serialization ---

const CANONICAL_HEADERS = [
  "timestamp",
  "sender",
  "recipient",
  "body",
  "attachments",
] as const;

function csvEscape(cell: string): string {
  if (/[",\n\r]/.test(cell)) {
    return `"${cell.replace(/"/g, '""')}"`;
  }
  return cell;
}

function rowsToCsv(rows: CanonicalRow[]): string {
  const header = CANONICAL_HEADERS.join(",") + "\n";
  if (rows.length === 0) return header;
  const body = rows
    .map((r) => CANONICAL_HEADERS.map((h) => csvEscape(r[h])).join(","))
    .join("\n");
  return header + body + "\n";
}

// --- Case meta builder ---

const TODO_CONTEXT_SENTINEL =
  "TODO: describe the context (e.g. 'their 9-year-old son Murphy')";
const TODO_NOTES_SENTINEL = "TODO: optional freeform notes about this corpus";

function formatTimePeriod(startIso: string, endIso: string): string {
  if (!startIso || !endIso) return "";
  const a = DateTime.fromISO(startIso, { zone: "utc" }).toFormat("LLL yyyy");
  const b = DateTime.fromISO(endIso, { zone: "utc" }).toFormat("LLL yyyy");
  return a === b ? a : `${a} – ${b}`;
}

function buildCaseMeta(corpusId: string, stats: IngestStats): CaseMeta {
  return {
    id: corpusId,
    privacy_class: "private",
    prompt_vars: {
      party_a: stats.party_a,
      party_b: stats.party_b,
      format: "Civil Communicator PDF export, canonicalized to CSV (schema v1)",
      time_period: formatTimePeriod(
        stats.date_range.start ?? "",
        stats.date_range.end ?? "",
      ),
      context: TODO_CONTEXT_SENTINEL,
    },
    known_patterns: [],
    notes: TODO_NOTES_SENTINEL,
  };
}

// --- Public entry point ---

export interface IngestOptions {
  corpusId: string;
  onProgress?: (p: PdfExtractProgress) => void;
}

/**
 * Pure-text parsing path: takes already-extracted page texts and returns
 * the canonical IngestResult. Exposed so unit tests can exercise the
 * parser without running pdf.js in Node. The PDF extraction step is the
 * same shared path used by the OFW adapter — its fidelity is validated
 * separately via live click-through smoke.
 */
export function ingestCivilCommunicatorFromPages(
  pagesRaw: string[],
  opts: { corpusId: string },
): IngestResult {
  const pages = cleanPages(pagesRaw);
  const combined = concatenatePages(pages);

  if (pages.length === 0) {
    throw new MalformedCivilCommunicatorError("PDF contained no extractable text");
  }

  const rawStanzas = splitStanzas(combined);
  if (rawStanzas.length === 0) {
    throw new MalformedCivilCommunicatorError(
      "no 'Comm ID N' stanzas found — likely wrong PDF format",
    );
  }

  const parsed: ParsedStanza[] = [];
  const warnings: string[] = [];
  let malformedCount = 0;
  for (const raw of rawStanzas) {
    try {
      parsed.push(parseStanzaFields(raw));
    } catch (e) {
      malformedCount += 1;
      const msg = e instanceof Error ? e.message : String(e);
      warnings.push(`malformed stanza skipped: ${msg}`);
    }
  }
  if (rawStanzas.length > 0 && malformedCount / rawStanzas.length > 0.1) {
    throw new MalformedCivilCommunicatorError(
      `more than 10% of stanzas malformed (${malformedCount}/${rawStanzas.length}); likely wrong format`,
    );
  }

  // Detect duplicate comm_ids (log only; dedup happens in buildRowsAndMetadata).
  const seen = new Set<string>();
  for (const p of parsed) {
    if (seen.has(p.comm_id)) {
      warnings.push(`duplicate comm_id skipped: ${p.comm_id}`);
    }
    seen.add(p.comm_id);
  }

  const { rows, metas, stats } = buildRowsAndMetadata(parsed);
  const csv = rowsToCsv(rows);
  const case_meta = buildCaseMeta(opts.corpusId, stats);

  return {
    rows,
    source_metas: metas,
    stats,
    csv,
    case_meta,
    warnings,
  };
}

/**
 * Ingest a Civil Communicator PDF export into the canonical CSV shape.
 *
 * Throws MalformedCivilCommunicatorError if the PDF doesn't contain the
 * Comm-ID stanzas, or if >10% of stanzas fail to parse (likely wrong format).
 */
export async function ingestCivilCommunicatorPdf(
  pdfData: ArrayBuffer | Uint8Array,
  opts: IngestOptions,
): Promise<IngestResult> {
  const pagesRaw = await extractPdfTextEol(pdfData, opts.onProgress);
  return ingestCivilCommunicatorFromPages(pagesRaw, { corpusId: opts.corpusId });
}
